import React, { useEffect, useState, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import EmbeddedFieldComponent from './embedded-field.component';
import { SecureDocsService } from '../../services/SecureDocsService';
import { mapToSecureDocUpdateInfo, SecureDocUpdateInfoModel } from '../../models/secure-docs.model';
import setToast from '../../helpers/toaster.helper';
import { ToasterType } from '../shared/toaster/toaster.model';
import { ProfileModel } from '../../models/profile.model';
import { UserService } from '../../services/UserService';
import { useSelector } from "react-redux";
import * as selector from '../../selectors/selectors';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface DroppedField {
    id: string;
    type: string;
    label: string;
    x: number;
    y: number;
    width: number;
    height: number;
    page?: number;
    assignedRecipient?: string;
}

interface SecureDocViewProps {
    currentPage?: number;
    fileID?: string;
    onPageChange?: (page: number) => void;
    onLoadSuccess?: (numPages: number) => void;
    onScaleChange?: (scale: number) => void;
    showNavigation?: boolean;
    width?: number;
    fields?: DroppedField[];
    selectedFieldId?: string | null;
    onFieldSelect?: (id: string | null) => void;
    onFieldDelete?: (id: string) => void;
    onFieldDragStart?: (field: DroppedField, startPos: { x: number; y: number }) => void;
    onFieldUpdate?: (id: string, updates: Partial<DroppedField>) => void;
    recipientRestrictions?: Map<string, { canEdit: boolean, canAdd: boolean }>;
    onConfiguredFieldsLoad?: (fields: DroppedField[], recipients: any[]) => void;
    recipientEmails?: { recipient_id: string, email: string; order_number: number }[];
    pdfBase64?: string | null;
}

const SecureDocViewComponent: React.FC<SecureDocViewProps> = ({
    currentPage: propCurrentPage,
    fileID,
    onPageChange,
    onLoadSuccess,
    onScaleChange,
    showNavigation = true,
    width = 800,
    fields = [],
    selectedFieldId,
    onFieldSelect,
    onFieldDelete,
    onFieldDragStart,
    onFieldUpdate,
    recipientRestrictions,
    onConfiguredFieldsLoad,
    recipientEmails,
    pdfBase64,
}) => {
    const borderColors = ["#156fb3", "#7A14FF", "#00C48C", "#0091FF", "#FF647C"];
    const [pdfData, setPdfData] = useState<Uint8Array | null>(null);
    const [numPages, setNumPages] = useState<number>(0);
    const [internalCurrentPage, setInternalCurrentPage] = useState<number>(1);
    const [fileId, setFileId] = useState<string | undefined>(fileID);
    const [isLoadingUpdateData, setIsLoadingUpdateData] = useState<boolean>(false);
    const currentPage = propCurrentPage !== undefined ? propCurrentPage : internalCurrentPage;
    const [updateData, setUpdateData] = useState<SecureDocUpdateInfoModel | null>(null);
    const fileData = useMemo(() => (pdfData ? { data: pdfData } : null), [pdfData]);
    const Profile: ProfileModel = UserService.getCurrentUserFromLocalStorage();
    const [agreementData, setAgreementData] = useState<any>(null);
    const [configuredFields, setConfiguredFields] = useState<DroppedField[]>([]);
    const [internalRecipientEmails, setInternalRecipientEmails] = useState<{ recipient_id: string, email: string; order_number: number }[]>([]);
    const currentRecipientEmails = recipientEmails || internalRecipientEmails;
    const IsMobileDevice: boolean = useSelector(selector.isMobileDevice);
    const IsDarkMode: boolean = useSelector(selector.isDarkMode);

    useEffect(() => {
        if (!fileId && pdfBase64) {
            try {
                const byteArray = Uint8Array.from(atob(pdfBase64), (c) => c.charCodeAt(0));
                setPdfData(byteArray);
            } catch (e) {
                setToast("Error loading PDF", ToasterType.DANGER);
            }
        }
    }, [fileId, pdfBase64]);

    useEffect(() => {
        if (fileId) {
            const fetchConfigurationData = async () => {
                setIsLoadingUpdateData(true);
                let convertedFields: DroppedField[] = [];
                let formattedRecipients: { recipient_id: string, email: string; order_number: number }[] = [];

                try {
                    const configResponse = await SecureDocsService.getFileConfigurations(fileId);
                    if (configResponse.data.file) {
                        const pdfBase64 = configResponse.data.file;
                        const byteCharacters = atob(pdfBase64);
                        const byteArray = Uint8Array.from(byteCharacters, (c) => c.charCodeAt(0));
                        setPdfData(byteArray);
                    }

                    if (configResponse.data.elements && Array.isArray(configResponse.data.elements)) {
                        convertedFields = configResponse.data.elements.map((element: any) => ({
                            id: element.element_id || `field-${Date.now()}-${Math.random()}`,
                            type: element.element_type.toLowerCase(),
                            label: element.element_type,
                            x: Number(element.placeholder.x) || 0,
                            y: Number(element.placeholder.y) || 0,
                            width: Number(element.placeholder.width) || 150,
                            height: Number(element.placeholder.height) || 40,
                            page: Number(element.placeholder.page) || 1,
                            assignedRecipient: element.assigned_recipient.recipient_id || element.assigned_recipient
                        }));

                        setConfiguredFields(convertedFields);
                    }

                    if (configResponse.data.recipients && Array.isArray(configResponse.data.recipients)) {
                        formattedRecipients = configResponse.data.recipients.map((recipient: any) => ({
                            recipient_id: recipient.recipient_id,
                            email: recipient.email,
                            order_number: Number(recipient.order_number) || 1
                        }));

                        setInternalRecipientEmails(formattedRecipients);
                    }

                } catch (error) {
                    setToast("Failed to load document configuration", ToasterType.DANGER);
                } finally {
                    setIsLoadingUpdateData(false);
                    if (onConfiguredFieldsLoad && (convertedFields.length > 0 || formattedRecipients.length > 0)) {
                        onConfiguredFieldsLoad(convertedFields, formattedRecipients);
                    }
                }
            };

            fetchConfigurationData();
        }
    }, [fileId, onConfiguredFieldsLoad]);

    const handlePageChange = (newPage: number) => {
        if (onPageChange) {
            onPageChange(newPage);
        } else {
            setInternalCurrentPage(newPage);
        }
    };

    const goToPreviousPage = () => {
        handlePageChange(Math.max(currentPage - 1, 1));
    };

    const goToNextPage = () => {
        handlePageChange(Math.min(currentPage + 1, numPages));
    };

    const goToFirstPage = () => {
        handlePageChange(1);
    };

    const goToLastPage = () => {
        handlePageChange(numPages);
    };

    const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const pageNum = parseInt(e.target.value);
        if (pageNum >= 1 && pageNum <= numPages) {
            handlePageChange(pageNum);
        }
    };

    const handleLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
        if (onLoadSuccess) {
            onLoadSuccess(numPages);
        }
        if (onScaleChange) {
            onScaleChange(1);
        }
    };

    const allFields = useMemo(() => {
        if (fields.length > 0) {
            return fields;
        }
        return configuredFields;
    }, [fields, configuredFields]);

    const currentPageFields = allFields.filter(field => (field.page || 1) === currentPage);

    const editableFields = currentPageFields.filter(field =>
        !field.assignedRecipient || !recipientRestrictions?.get(field.assignedRecipient) ||
        recipientRestrictions.get(field.assignedRecipient)?.canEdit !== false
    );

    const lockedFields = currentPageFields.filter(field =>
        field.assignedRecipient && recipientRestrictions?.get(field.assignedRecipient)?.canEdit === false
    );

    const fieldsByRecipient = useMemo(() => {
        const grouped = new Map();
        currentPageFields.forEach(field => {
            if (field.assignedRecipient) {
                if (!grouped.has(field.assignedRecipient)) {
                    grouped.set(field.assignedRecipient, []);
                }
                grouped.get(field.assignedRecipient).push(field);
            }
        });
        return grouped;
    }, [currentPageFields]);

    if (isLoadingUpdateData) {
        return (
            <div className="d-flex justify-center align-center t-align-center p-relative h-v-100">
                <div className="load-config-container">
                    <div className="loading-spinner mx-auto"></div>
                    <p className="success-page-text pt-2 pl-3 color-primary-accent h4-semibold">Loading Document Configuration...</p>
                    <p className="success-page-text pt-1 pl-3 color-primary-accent h4-semibold">Please wait while we retrieve your document and field configurations</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`w-p-100 0-hidden d-flex flex-direction  ${IsMobileDevice ? '' : "h-v-80"}`}>
            {pdfData ? (
                <>
                    {showNavigation && numPages > 1 && (
                        <div className={`pdf-navigation d-flex align-center justify-center bg-color-secondary h6-medium`}>
                            <button
                                className="pdf-nav-button b-none h6-medium c-pointer"
                                onClick={goToFirstPage}
                                disabled={currentPage === 1}
                            >
                                ⏮️ First
                            </button>

                            <button
                                className="pdf-nav-button b-none h6-medium c-pointer"
                                onClick={goToPreviousPage}
                                disabled={currentPage === 1}
                            >
                                ⬅️ Previous
                            </button>

                            <div className="pdf-page-input-wrapper d-flex align-center">
                                <span className="h6-medium color-primary-dark">Page</span>
                                <input
                                    type="number"
                                    className="pdf-page-input h6-medium t-align-center color-primary-dark"
                                    value={currentPage}
                                    onChange={handlePageInputChange}
                                    min={1}
                                    max={numPages}
                                />
                                <span className="h6-medium color-primary-dark">of {numPages}</span>
                            </div>

                            <button
                                className="pdf-nav-button b-none h6-medium c-pointer"
                                onClick={goToNextPage}
                                disabled={currentPage === numPages}
                            >
                                Next ➡️
                            </button>

                            <button
                                className="pdf-nav-button b-none h6-medium c-pointer"
                                onClick={goToLastPage}
                                disabled={currentPage === numPages}
                            >
                                Last ⏭️
                            </button>
                        </div>
                    )}

                    <div className='t-align-center p-relative flex-1 o-y-auto o-x-hidden d-flex justify-center align-start'>
                        <div className="pdf-page-wrapper">
                            <Document
                                file={fileData}
                                onLoadSuccess={handleLoadSuccess}
                                loading={
                                    <div className="load-config-container">
                                        <div className="loading-spinner mx-auto"></div>
                                        <div className='h4-semibold'>Loading PDF document...</div>
                                    </div>
                                }
                                error={
                                    <div className="load-config-container">
                                        <div className="pdf-error-icon">❌</div>
                                        <div className='h4-semibold'>Error loading PDF document</div>
                                    </div>
                                }
                            >
                                <Page
                                    pageNumber={currentPage}
                                    width={width}
                                    renderTextLayer={false}
                                    renderAnnotationLayer={false}
                                    className="react-pdf__Page"
                                    data-page-number={currentPage}
                                    loading={
                                        <div className=" h4-semibold">
                                            Loading page {currentPage}...
                                        </div>
                                    }
                                />
                            </Document>

                            {currentPageFields.map((field) => (
                                <EmbeddedFieldComponent
                                    key={field.id}
                                    field={field}
                                    isSelected={selectedFieldId === field.id}
                                    onSelect={onFieldSelect}
                                    onDelete={onFieldDelete}
                                    onDragStart={onFieldDragStart}
                                    onResize={(id, width, height) => onFieldUpdate && onFieldUpdate(id, { width, height })}
                                    recipientRestrictions={recipientRestrictions}
                                    recipientEmails={currentRecipientEmails}
                                />
                            ))}
                        </div>
                    </div>

                    <div className={`display-page-info display-page-info t-align-center ${IsDarkMode ? "bg-color-sidebar-bg" : "bg-color-secondary"} color-primary-accent h6-bold`}>
                        <div >
                            Page {currentPage} of {numPages} • {currentPageFields.length} field{currentPageFields.length !== 1 ? 's' : ''}
                        </div>
                    </div>
                </>
            ) : (
                <div className="no-pdf-container d-flex flex-direction align-center justify-center t-align-center h4-semibold">
                    <p className="h3-semibold mb-1">
                        No PDF Document Found
                    </p>
                    <p className="h4-semibold mb-2 opacity-3">
                        Please upload a PDF document or check the file configuration
                    </p>
                </div>
            )}
        </div>
    );
};

export default SecureDocViewComponent;